import { useFormik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { ArrowLeft, Eye, EyeOff, LockKeyhole } from "lucide-react";
import { setInitialPassword } from "../../Actions/UserActions";
import PasswordStrengthRules, {
  isPasswordValid,
} from "../Common/PasswordStrengthRules";
import CustomSnackbar from "../../Sections/Common/snackBar";
import NetcluesBrandLogo from "../../../src/Assets/svg/favicon.svg";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import "./SetInitialPassword.css";

const { VALIDATION } = FRONTEND_MESSAGES;

const validationSchema = yup.object().shape({
  newPassword: yup
    .string()
    .required(VALIDATION.REQUIRED_FIELD("new password"))
    .min(8, VALIDATION.MIN_LENGTH("password", 8))
    .max(20, VALIDATION.MAX_LENGTH("password", 20)),
  confirmNewPassword: yup
    .string()
    .required(VALIDATION.REQUIRED_FIELD("confirm password"))
    .oneOf([yup.ref("newPassword")], "Passwords do not match"),
});

const SetInitialPassword = ({ token }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const formik = useFormik({
    initialValues: { newPassword: "", confirmNewPassword: "" },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: handleSetPassword,
  });

  async function handleSetPassword(values) {
    if (!isPasswordValid(values.newPassword)) {
      setSnackbar({
        open: true,
        message: "Password does not meet all requirements",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const res = await setInitialPassword({
        token,
        newPassword: values.newPassword,
      });

      setSnackbar({
        open: true,
        message: res.data.message || "Password set successfully!",
        severity: "success",
      });

      setTimeout(() => {
        handleCloseSnackbar();
        navigate("/login");
      }, 1500);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to set password",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  const canSubmit =
    !loading &&
    formik.values.newPassword &&
    formik.values.confirmNewPassword &&
    !formik.errors.newPassword &&
    !formik.errors.confirmNewPassword &&
    isPasswordValid(formik.values.newPassword);

  return (
    <div className="sip">
      <div className="sip-card">
        <div className="sip-brand">
          <img src={NetcluesBrandLogo} alt="Netclues" />
        </div>

        <div className="sip-icon" aria-hidden>
          <LockKeyhole size={26} strokeWidth={1.85} />
        </div>

        <h1>Set your password</h1>
        <p className="sip-lead">
          Create a strong password to finish activating your account.
        </p>

        <form onSubmit={formik.handleSubmit} noValidate>
          <div className="sip-field">
            <label className="sip-label" htmlFor="sip-new-password">
              New password
            </label>
            <TextField
              id="sip-new-password"
              fullWidth
              type={showNewPassword ? "text" : "password"}
              name="newPassword"
              placeholder="Enter new password"
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.newPassword && Boolean(formik.errors.newPassword)
              }
              helperText={
                formik.touched.newPassword && formik.errors.newPassword
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      aria-label={
                        showNewPassword ? "Hide password" : "Show password"
                      }
                      onClick={() => setShowNewPassword((v) => !v)}
                      edge="end"
                    >
                      {showNewPassword ? (
                        <EyeOff size={18} strokeWidth={1.9} />
                      ) : (
                        <Eye size={18} strokeWidth={1.9} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          {formik.values.newPassword ? (
            <div className="sip-rules">
              <PasswordStrengthRules
                password={formik.values.newPassword}
                show
                inline
              />
            </div>
          ) : null}

          <div className="sip-field">
            <label className="sip-label" htmlFor="sip-confirm-password">
              Confirm password
            </label>
            <TextField
              id="sip-confirm-password"
              fullWidth
              type={showConfirmPassword ? "text" : "password"}
              name="confirmNewPassword"
              placeholder="Re-enter password"
              value={formik.values.confirmNewPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.confirmNewPassword &&
                Boolean(formik.errors.confirmNewPassword)
              }
              helperText={
                formik.touched.confirmNewPassword &&
                formik.errors.confirmNewPassword
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      aria-label={
                        showConfirmPassword
                          ? "Hide confirm password"
                          : "Show confirm password"
                      }
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      edge="end"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={18} strokeWidth={1.9} />
                      ) : (
                        <Eye size={18} strokeWidth={1.9} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <button
            type="submit"
            className="sip-submit"
            disabled={!canSubmit}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Set password"
            )}
          </button>
        </form>

        <button
          type="button"
          className="sip-back"
          onClick={() => navigate("/login")}
        >
          <ArrowLeft size={15} strokeWidth={2} aria-hidden />
          Back to login
        </button>

        <p className="sip-note">
          Use 8–20 characters with upper and lower case, a number, and a special
          character.
        </p>
      </div>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </div>
  );
};

export default SetInitialPassword;
